/**
 * Pure helpers for the keybindings store's override edits: building the next
 * override map, and working out which chords a change ADDED to a command —
 * the only chords that win collisions and can be refused as reserved (D6).
 * "Added" is decided by chord identity (`sameChordOnLayout`), not spelling.
 */
import { sameChordOnLayout } from '../model/chordIdentity';
import { chordsLostToSavedOverrides } from '../model/savedOverrideLosses';
import type { KeybindingCommandId, KeybindingNotice, KeyboardLayoutSnapshot, Platform } from '../model/keybindingTypes';
import type { KeybindingOverrideMap } from './keybindingsStoreTypes';

export function withOverride(
  overrides: KeybindingOverrideMap,
  commandId: KeybindingCommandId,
  chords: readonly string[] | undefined,
): KeybindingOverrideMap {
  const next: Record<string, readonly string[]> = { ...overrides };
  if (chords === undefined) delete next[commandId];
  else next[commandId] = chords;
  return next;
}

/**
 * N1/O1: saving a row that still used its defaults leaves out every chord that
 * was inactive before the change because it lost to another command's saved
 * override (whatever the notice kind) — the user taking the row accepts the
 * outcome already in effect. A row that is already saved keeps its chords, and
 * chords this change adds stay (a reclaim).
 */
export function withoutChordsLostToOverrides(
  chords: readonly string[],
  commandId: KeybindingCommandId,
  notices: readonly KeybindingNotice[],
  overrides: KeybindingOverrideMap,
  added: readonly string[],
): readonly string[] {
  if (Array.isArray(overrides[commandId])) return chords;
  const lost = chordsLostToSavedOverrides(notices, commandId, overrides);
  return lost.size === 0 ? chords : chords.filter((chord) => !lost.has(chord) || added.includes(chord));
}

export function pickOverrides(
  overrides: KeybindingOverrideMap,
  keep: (commandId: string) => boolean,
): KeybindingOverrideMap {
  return Object.fromEntries(Object.entries(overrides).filter(([id]) => keep(id)));
}

/** The stored chord that is the same keys as `chord` here, if the list holds one. */
export function findSameChord(
  chords: readonly string[],
  chord: string,
  platform: Platform,
  layout: KeyboardLayoutSnapshot,
): string | undefined {
  return chords.find((stored) => sameChordOnLayout(stored, chord, platform, layout));
}

/**
 * Chords in `after` that are not the same keys as any chord in `before` —
 * re-spelling a stored `Ctrl+Q` as `Mod+Q` off macOS, or a physical
 * `Ctrl+[Backquote]` as `Ctrl+\`` on a US layout, adds nothing.
 */
export function addedChords(
  before: readonly string[],
  after: readonly string[],
  platform: Platform,
  layout: KeyboardLayoutSnapshot,
): string[] {
  return after.filter((chord) => findSameChord(before, chord, platform, layout) === undefined);
}
